import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { businessAccountsApi, metaApi, ApiError } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { useAuth } from '@/components/AuthProvider';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { getLimitErrorMessage, isLimitExceededError } from '@/hooks/useFeatureLimits';

export interface BusinessAccount {
  id: string;
  name: string;
  phone_number: string;
  phone_number_id: string;
  business_account_id: string;
  access_token_encrypted: string;
  is_active: boolean;
  is_verified?: boolean;
  quality_rating?: string;
  connection_type?: string | null;
  // WABA lock state — populated by the send path when Meta returns error 131031
  // ("Account has been locked"). null/undefined = healthy; "LOCKED" = blocked.
  lock_status?: string | null;
  lock_detected_at?: string | null;
  // Per-BA Add Lead modal layout (see lib/constants/leadFormFields.ts). null
  // means "use baseline". Wholesale write/replace — never deep-merged.
  lead_form_layout?: unknown | null;
  created_at: string;
  updated_at: string;
}

export function useBusinessAccounts() {
  const { user } = useAuth();

  const query = useQuery<BusinessAccount[]>({
    queryKey: ['business-accounts-v2', user?.id || 'no-user'],
    queryFn: async (): Promise<BusinessAccount[]> => {
      const result = await businessAccountsApi.getAll({
        is_active: true
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return Array.isArray(result.data) ? result.data : [];
    },
    enabled: !!user, // Only fetch when user is available
    staleTime: 2 * 60 * 1000, // Data stays fresh for 2 minutes (reduced from 5 to get quality updates faster)
    gcTime: 10 * 60 * 1000, // Cache for 10 minutes
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: false, // Don't refetch on mount if data exists
  });

  return query;
}

export function useCreateBusinessAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accountData: {
      name: string;
      phone_number: string;
      phone_number_id: string;
      business_account_id: string;
      access_token: string;
    }) => {
      const result = await businessAccountsApi.create({
        name: accountData.name,
        phone_number: accountData.phone_number,
        phone_number_id: accountData.phone_number_id,
        business_account_id: accountData.business_account_id,
        access_token_encrypted: accountData.access_token, // In production, encrypt this
      });

      if (result.error) {
        throw ApiError.fromResult(result);
      }

      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
      toast({ variant: "success",
        title: "Business account added",
        description: "WhatsApp Business account has been successfully configured.",
      });
    },
    onError: (error: any) => {
      const errorMessage = getLimitErrorMessage(error);
      toast({
        title: isLimitExceededError(error) ? "Limit Reached" : "Error",
        description: errorMessage,
        variant: "destructive",
        duration: isLimitExceededError(error) ? 8000 : 5000,
      });
    }
  });
}

export function useUnlinkBusinessAccount() {
  const queryClient = useQueryClient();
  const { setSelectedAccountId } = useBusinessAccountContext();

  return useMutation({
    mutationFn: async (accountId: string) => {
      try {
        const result = await metaApi.unlinkBusinessAccount(accountId);

        if (result.error) {
          throw ApiError.fromResult(result);
        }

        return result.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data, accountId) => {
      // Clear selected account if it was the unlinked one
      const currentSelected = localStorage.getItem('selectedBusinessAccountId');
      if (currentSelected === accountId) {
        setSelectedAccountId(null);
        localStorage.removeItem('selectedBusinessAccountId');
      }

      queryClient.invalidateQueries({ queryKey: ['business-accounts-v2'] });
      toast({
        title: "Business account delinked",
        description: "Business account has been successfully delinked from Meta OAuth. You can reconnect it anytime.",
      });
    },
    onError: (error) => {
      showApiError(error, { fallback: { title: "Error" } });
    }
  });
}
